/** @jsxImportSource @emotion/react */

import { jsonToCsv } from '@quickbit/backoffice-core-frontend';
import { UtilityButton } from '@quickbit/qb-design-system';
import { useAsyncFn } from 'react-use';
import {
  MerchantRefundsFilters,
  Refund,
  RefundsSortOptions,
  searchRefunds,
} from '../api';

const PAGE_SIZE = 20;

interface Props {
  filters: MerchantRefundsFilters;
  sortOptions?: RefundsSortOptions;
}

type Columns = 'Created At' | 'Order ID' | 'Refund Amount';

type Row = Record<Columns, string | number | null>;

const refundToRow = (refund: Refund): Row => {
  return {
    'Created At': refund.createdAt.toLocaleString(),
    'Order ID': refund.orderId,
    'Refund Amount': refund.refundAmount.toString() + ' ' + refund.currency,
  };
};

export const ExportCsv = ({ filters, sortOptions }: Props) => {
  const [{ loading: downloadCsvLoading }, downloadCsv] =
    useAsyncFn(async () => {
      const page = 1;
      const firstRefunds = await searchRefunds({
        pagination: { size: PAGE_SIZE, page },
        filters,
        sortOptions,
      });
      let data: Row[] = firstRefunds.refunds.map((refund) =>
        refundToRow(refund)
      );
      const lastPage = Math.ceil(
        firstRefunds.aggregatedData.reduce((accumulator, current) => {
          return accumulator + current.refundCount;
        }, 0) || 0 / PAGE_SIZE
      );
      let i = page + 1;
      let errorCount = 0;
      while (i <= lastPage) {
        try {
          const refundsResponse = await searchRefunds({
            pagination: { size: PAGE_SIZE, page: i },
            filters,
            sortOptions,
          });
          data = data.concat(
            refundsResponse.refunds.map((refund) => refundToRow(refund))
          );
          i++;
          errorCount = 0;
        } catch (e) {
          errorCount++;
          if (errorCount >= 5) {
            throw new Error('Failed to fetch order data');
          }
          console.error('Failed to fetch order data, trying again', e);
        }
      }

      jsonToCsv({
        data: data,
        filename: 'refunds',
        delimiter: ';',
        columns: ['Created At', 'Order ID', 'Refund Amount'] as Columns[],
      });
      return data;
    }, [filters, sortOptions]);

  return (
    <UtilityButton
      text="Extract as csv"
      onClick={downloadCsv}
      loading={downloadCsvLoading}
      disabled={downloadCsvLoading}
    />
  );
};
